import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDateRange, calculateDatesFor } from './lib/date-range';
import DateInput from './DateInput';
import DateRangeInputs from './DateRangeInputs';
import Label from './Label';
import { Select } from './Select';

const Wrapper = styled.div`
  align-items: stretch;
  display: flex;

  ${({ stacked }) =>
    stacked
      ? `
          flex-direction: column;

          ${Select} {
            margin-bottom: 8px;
            max-width: unset;
          }
        `
      : `
          flex-direction: row;

          ${Select} {
            margin-right: 16px;
            max-width: 180px;
            min-width: 180px;
          }

          ${DateRangeInputs} {
            margin-top: 4px;
          }

          ${DateInput} {
            width: 125px;
          }
        `}
`;

function DateRangePicker({
  calculate,
  className,
  onChange,
  options = [],
  portal = false,
  stacked = false,
  value: initial = {},
  ...rest
}) {
  const [{ range, start, end }, setValue] = useDateRange(
    options,
    initial,
    calculate
  );

  const handleTypeChange = useCallback(
    ({ value: selected }) => {
      const next = calculateDatesFor(selected, calculate);
      setValue(next);
      onChange?.(next);
    },
    [calculate, onChange, setValue]
  );

  const handleDateChange = useCallback(
    dates => {
      const next = { range: 'custom', ...dates };
      setValue(next);
      onChange?.(next);
    },
    [onChange, setValue]
  );

  return (
    <Wrapper className={className} stacked={stacked}>
      <Label htmlFor="range">
        Date Range
        <Select
          name="range"
          onChange={handleTypeChange}
          options={options}
          portal={portal}
          value={options.find(({ value: selected }) => selected === range)}
        />
      </Label>

      <DateRangeInputs
        {...rest}
        disabled={range !== 'custom'}
        onChange={handleDateChange}
        portal={portal}
        value={{ start, end }}
      />
    </Wrapper>
  );
}

DateRangePicker.propTypes = {
  ...DateRangeInputs.propTypes,
  calculate: PropTypes.func,
  className: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  portal: PropTypes.bool,
  stacked: PropTypes.bool,
  value: PropTypes.shape({
    range: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
  }),
};

export default styled(DateRangePicker)``;
